import React, { useState } from 'react';
import { DONATION_AMOUNTS, DONATION_CONFIG } from '../constants/donationAmounts';

/**
 * SimpleDonationButton Component
 * Provides standalone Stripe checkout for donations
 */
const SimpleDonationButton = ({ 
  announcement, 
  center = { code: 'DEZ', name: 'Dar-e-Zainab (AS)' },
  userEmail 
}) => {
  const [showAmounts, setShowAmounts] = useState(true); // Start with amounts showing
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Default suggested amounts
  const suggestedAmounts = announcement?.suggested_amounts || DONATION_AMOUNTS.default;

  const handleQuickCheckout = async () => {
    setError(null);
    setIsProcessing(true);

    // Determine the final amount
    const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;

    if (!finalAmount || finalAmount < 1) {
      setError('Please select or enter a valid amount (minimum $1)');
      setIsProcessing(false);
      return;
    }

    try {
      // Call the Netlify Function to create checkout session
      const response = await fetch('/.netlify/functions/create-donation-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: finalAmount,
          announcementId: announcement?.id,
          announcementTitle: announcement?.title,
          centerCode: center.code,
          centerName: center.name,
          donorEmail: userEmail,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (!data?.url) {
        throw new Error('No checkout URL received');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to create checkout session. Please try again.');
      setIsProcessing(false);
    }
  };

  // If sponsor is not enabled and no announcement, don't show button
  if (announcement && !announcement.sponsor_enabled) {
    return null;
  }

  return (
    <div style={{
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '12px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ animation: 'slideIn 0.3s ease' }}>
          {/* Sponsor Message */}
          {announcement?.sponsor_message && (
            <p style={{
              marginBottom: '20px',
              padding: '12px',
              background: 'white',
              borderLeft: '4px solid #667eea',
              borderRadius: '4px',
              fontStyle: 'italic',
              color: '#555',
            }}>
              {announcement.sponsor_message}
            </p>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              color: '#c00',
              fontSize: '14px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#fee',
              borderRadius: '8px',
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Amount Selection */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ marginBottom: '12px', color: '#333', fontSize: '16px' }}>
              Select an amount:
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px',
              marginBottom: '20px',
            }}>
              {suggestedAmounts.map((amount) => (
                <button
                  key={amount}
                  style={{
                    padding: '14px 16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    background: selectedAmount === amount ? '#667eea' : 'white',
                    color: selectedAmount === amount ? 'white' : '#333',
                    border: `2px solid ${selectedAmount === amount ? '#667eea' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                    setError(null);
                  }}
                  disabled={isProcessing}
                  onMouseEnter={(e) => {
                    if (selectedAmount !== amount) {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.background = '#f0f4ff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAmount !== amount) {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  ${amount}
                </button>
              ))}
            </div>

            {/* Tax-deductible Helper Text */}
            {DONATION_CONFIG.showTaxDeductibleText && (
              <p style={{
                fontSize: '12px',
                color: '#888',
                textAlign: 'center',
                marginTop: '-12px',
                marginBottom: '16px'
              }}>
                {DONATION_CONFIG.taxDeductibleMessage}
              </p>
            )}

            {/* Custom Amount Input */}
            <div>
              <label htmlFor="custom-amount" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#666',
              }}>
                Or enter a custom amount:
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                <span style={{
                  padding: '0 12px',
                  fontSize: '18px',
                  color: '#666',
                  background: '#f5f5f5',
                }}>
                  $
                </span>
                <input
                  id="custom-amount"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                    setError(null);
                  }}
                  disabled={isProcessing}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && (customAmount || selectedAmount)) {
                      handleQuickCheckout();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '16px',
                    border: 'none',
                    outline: 'none',
                    background: 'white',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Quick Checkout Button */}
          <button
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: isProcessing || (!selectedAmount && !customAmount) ? 'not-allowed' : 'pointer',
              opacity: isProcessing || (!selectedAmount && !customAmount) ? 0.5 : 1,
              transition: 'all 0.2s',
              marginBottom: '12px',
            }}
            onClick={handleQuickCheckout}
            disabled={isProcessing || (!selectedAmount && !customAmount)}
          >
            {isProcessing ? (
              <>⏳ Creating checkout...</>
            ) : (
              <>
                ⚡ Quick Checkout
                <span style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  opacity: 0.9,
                  marginTop: '4px',
                }}>
                  Apple Pay, Google Pay, Card
                </span>
              </>
            )}
          </button>

          {/* Cancel Button */}
          <button
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              color: '#666',
              background: 'transparent',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => {
              setShowAmounts(false);
              setSelectedAmount(null);
              setCustomAmount('');
              setError(null);
            }}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Cancel
          </button>
        </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SimpleDonationButton;